import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Download, FunnelSimple, CheckCircle, Circle } from '@phosphor-icons/react';
import { formatDate } from '../utils/dateFormat';

const DEFAULT_FILTERS = {
  jo_number: '',
  executive_name: '',
  customer_name: '',
  jo_date_from: '',
  jo_date_to: '',
  pending_only: false,
  glass_status: 'all',   // all | ordered | received | not_ordered | not_received
  profile_status: 'all',
};

function statusToParams(filters) {
  const p = new URLSearchParams();
  if (filters.jo_number) p.append('jo_number', filters.jo_number);
  if (filters.executive_name) p.append('executive_name', filters.executive_name);
  if (filters.customer_name) p.append('customer_name', filters.customer_name);
  if (filters.jo_date_from) p.append('jo_date_from', filters.jo_date_from);
  if (filters.jo_date_to) p.append('jo_date_to', filters.jo_date_to);
  if (filters.pending_only) p.append('pending_only', 'true');
  // glass
  if (filters.glass_status === 'ordered') p.append('glass_ordered', 'true');
  if (filters.glass_status === 'not_ordered') p.append('glass_ordered', 'false');
  if (filters.glass_status === 'received') p.append('glass_received', 'true');
  if (filters.glass_status === 'not_received') p.append('glass_received', 'false');
  // profile
  if (filters.profile_status === 'ordered') p.append('profile_ordered', 'true');
  if (filters.profile_status === 'not_ordered') p.append('profile_ordered', 'false');
  if (filters.profile_status === 'received') p.append('profile_received', 'true');
  if (filters.profile_status === 'not_received') p.append('profile_received', 'false');
  return p;
}

function StatusCell({ entry }) {
  return entry?.done
    ? <CheckCircle size={18} weight="fill" className="text-emerald-600 inline" />
    : <Circle size={18} weight="bold" className="text-zinc-300 inline" />;
}

function downloadCSV(rows) {
  const headers = [
    'JO No', 'JO Date', 'Customer', 'Mobile', 'Executive', 'Delivery Date',
    'Status', 'Windows', 'Sqft', 'Profile Type', 'Glass Type',
    'Glass Ordered', 'Glass Order Supplier', 'Glass Order Date',
    'Glass Received', 'Glass Receipt Supplier', 'Glass Receipt Date',
    'Profile Ordered', 'Profile Order Supplier', 'Profile Order Date',
    'Profile Received', 'Profile Receipt Supplier', 'Profile Receipt Date',
    'Pending Items',
  ];
  const yn = (b) => (b ? 'Yes' : 'No');
  const csv = [headers.join(',')];
  for (const r of rows) {
    const line = [
      r.jo_number, r.jo_date, r.customer_name, r.customer_mobile, r.executive_name, r.delivery_date,
      r.status?.replace(/_/g, ' '), r.no_of_windows, r.total_sqft, r.profile_type, r.glass_type,
      yn(r.glass_ordered.done), r.glass_ordered.supplier || '', r.glass_ordered.date ? formatDate(r.glass_ordered.date) : '',
      yn(r.glass_received.done), r.glass_received.supplier || '', r.glass_received.date ? formatDate(r.glass_received.date) : '',
      yn(r.profile_ordered.done), r.profile_ordered.supplier || '', r.profile_ordered.date ? formatDate(r.profile_ordered.date) : '',
      yn(r.profile_received.done), r.profile_received.supplier || '', r.profile_received.date ? formatDate(r.profile_received.date) : '',
      (r.pending_items || []).join('; '),
    ].map(v => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    });
    csv.push(line.join(','));
  }
  const blob = new Blob([csv.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `procurement_tracking_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ProcurementReports() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusToParams(applied);
      const { data } = await api.get(`/api/reports/procurement-tracking?${params}`);
      setRows(data.rows || []);
      setCount(data.count || 0);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(err?.response?.data?.detail || 'Failed to load report');
      setRows([]); setCount(0);
    } finally { setLoading(false); }
  }, [applied]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = () => { setApplied({ ...filters }); };
  const handleReset = () => { setFilters(DEFAULT_FILTERS); setApplied(DEFAULT_FILTERS); };

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-4xl font-light tracking-tight text-zinc-950">Procurement Tracking Report</h1>
            <p className="text-sm text-zinc-500 mt-2">Glass & Profile order/receipt tracking across all Job Orders</p>
          </div>
          <Button
            data-testid="proc-report-export-csv"
            onClick={() => downloadCSV(rows)}
            disabled={!rows.length}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-11"
          >
            <Download size={18} weight="bold" className="mr-2" /> Export CSV ({rows.length})
          </Button>
        </div>

        <Card className="rounded-sm shadow-sm border-zinc-300">
          <CardHeader className="border-b border-zinc-300 py-3">
            <CardTitle className="text-base font-bold text-zinc-700 flex items-center gap-2">
              <FunnelSimple size={18} weight="bold" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">JO No</label>
              <Input data-testid="proc-report-filter-jo" value={filters.jo_number}
                onChange={e => setFilters({ ...filters, jo_number: e.target.value })}
                className="h-9 mt-1" placeholder="e.g. JO-1234" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Executive</label>
              <Input data-testid="proc-report-filter-exec" value={filters.executive_name}
                onChange={e => setFilters({ ...filters, executive_name: e.target.value })}
                className="h-9 mt-1" placeholder="Executive name" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Customer</label>
              <Input data-testid="proc-report-filter-customer" value={filters.customer_name}
                onChange={e => setFilters({ ...filters, customer_name: e.target.value })}
                className="h-9 mt-1" placeholder="Customer name" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">From Date</label>
              <Input type="date" data-testid="proc-report-filter-from" value={filters.jo_date_from}
                onChange={e => setFilters({ ...filters, jo_date_from: e.target.value })}
                className="h-9 mt-1" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">To Date</label>
              <Input type="date" data-testid="proc-report-filter-to" value={filters.jo_date_to}
                onChange={e => setFilters({ ...filters, jo_date_to: e.target.value })}
                className="h-9 mt-1" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Glass Status</label>
              <select data-testid="proc-report-filter-glass" value={filters.glass_status}
                onChange={e => setFilters({ ...filters, glass_status: e.target.value })}
                className="h-9 mt-1 w-full border border-zinc-300 rounded px-2 text-sm">
                <option value="all">All</option>
                <option value="ordered">Glass Ordered ✓</option>
                <option value="not_ordered">Glass Not Ordered</option>
                <option value="received">Glass Received ✓</option>
                <option value="not_received">Glass Not Received</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Profile Status</label>
              <select data-testid="proc-report-filter-profile" value={filters.profile_status}
                onChange={e => setFilters({ ...filters, profile_status: e.target.value })}
                className="h-9 mt-1 w-full border border-zinc-300 rounded px-2 text-sm">
                <option value="all">All</option>
                <option value="ordered">Profile Ordered ✓</option>
                <option value="not_ordered">Profile Not Ordered</option>
                <option value="received">Profile Received ✓</option>
                <option value="not_received">Profile Not Received</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm font-bold text-zinc-700 cursor-pointer">
                <input
                  data-testid="proc-report-filter-pending"
                  type="checkbox"
                  checked={filters.pending_only}
                  onChange={e => setFilters({ ...filters, pending_only: e.target.checked })}
                  className="h-4 w-4"
                />
                Pending only (any field not done)
              </label>
            </div>
            <div className="md:col-span-3 lg:col-span-4 flex gap-2 justify-end pt-2 border-t border-zinc-100">
              <Button variant="outline" onClick={handleReset} className="h-9">Reset</Button>
              <Button
                data-testid="proc-report-search"
                onClick={handleSearch}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-9"
              >
                {loading ? 'Loading...' : 'Search'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-sm shadow-sm border-zinc-300">
          <CardHeader className="border-b border-zinc-300 py-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-bold text-zinc-900">Results ({count})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-zinc-400">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-8 text-center text-zinc-400">No matching job orders.</div>
            ) : (
              <div className="overflow-x-auto">
                <table data-testid="proc-report-table" className="w-full text-sm">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">JO No</th>
                      <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Date</th>
                      <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Customer</th>
                      <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Executive</th>
                      <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Status</th>
                      <th className="text-center p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Glass<br />Ord.</th>
                      <th className="text-center p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Glass<br />Rec.</th>
                      <th className="text-center p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Profile<br />Ord.</th>
                      <th className="text-center p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Profile<br />Rec.</th>
                      <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.jo_number} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="p-2 font-bold text-zinc-900">{r.jo_number}</td>
                        <td className="p-2 text-zinc-700">{formatDate(r.jo_date) || r.jo_date}</td>
                        <td className="p-2 text-zinc-700">{r.customer_name}</td>
                        <td className="p-2 text-zinc-700">{r.executive_name || '—'}</td>
                        <td className="p-2">
                          <Badge className="bg-zinc-100 text-zinc-700 border border-zinc-200 text-xs">
                            {r.status?.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="p-2 text-center"><StatusCell entry={r.glass_ordered} /></td>
                        <td className="p-2 text-center"><StatusCell entry={r.glass_received} /></td>
                        <td className="p-2 text-center"><StatusCell entry={r.profile_ordered} /></td>
                        <td className="p-2 text-center"><StatusCell entry={r.profile_received} /></td>
                        <td className="p-2 text-zinc-600 text-xs">
                          {(r.pending_items || []).length === 0
                            ? <span className="text-emerald-700 font-bold">All done</span>
                            : r.pending_items.join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
